import 'css/src/components/app/aspect-ratio-picker';

import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';
import { getSupportedAspectRatios } from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';

type AspectRatioOptionProps = {
    aspectRatio: AspectRatio;
    label: string;
    isSelected: boolean;
    onSelect: (aspectRatio: AspectRatio) => void;
    tooltipText?: string;
};

const AspectRatioOption = React.memo(function AspectRatioOption({
    aspectRatio,
    label,
    isSelected,
    onSelect,
    tooltipText = '',
}: AspectRatioOptionProps) {
    const className = classNames('format-picker-wrapper', {
        active: isSelected,
    });

    const onClick = useCallback(() => {
        onSelect(aspectRatio);
    }, [onSelect, aspectRatio]);

    let optionNode = (
        <div className={className} key={aspectRatio} onClick={onClick}>
            <div className={`outer ${aspectRatio}`}>
                <div className="inner">
                    <span>{label}</span>
                </div>
            </div>
        </div>
    );

    if (tooltipText) {
        optionNode = (
            <Tooltip title={tooltipText} placement={Placement.TOP}>
                {optionNode}
            </Tooltip>
        );
    }

    return optionNode;
});

type AspectRatioPickerProps = {
    templateId: string | null;
    selectedAspectRatio: AspectRatio;
    onAspectRatioChange: (aspectRatio: AspectRatio) => void;
    containerClassName?: string;
};

const AspectRatioPicker = React.memo(function AspectRatioPicker({
    templateId,
    selectedAspectRatio,
    onAspectRatioChange,
    containerClassName,
}: AspectRatioPickerProps) {
    const supportedAspectRatios = useSelector((state: RootState) =>
        getSupportedAspectRatios(state, templateId),
    );

    useEffect(() => {
        if (
            supportedAspectRatios &&
            supportedAspectRatios.length > 0 &&
            !supportedAspectRatios.includes(selectedAspectRatio)
        ) {
            onAspectRatioChange(supportedAspectRatios[0]);
        }
    }, [supportedAspectRatios, selectedAspectRatio, onAspectRatioChange]);

    return (
        <div className={classNames('d-flex align-items-center gap-3', containerClassName)}>
            {supportedAspectRatios?.map((aspectRatio) => {
                return (
                    <AspectRatioOption
                        key={aspectRatio}
                        aspectRatio={aspectRatio}
                        label={VideoData.getAspectRatioDisplayRatio(aspectRatio)}
                        isSelected={selectedAspectRatio === aspectRatio}
                        onSelect={onAspectRatioChange}
                    />
                );
            })}
        </div>
    );
});

export default AspectRatioPicker;
